import { Column, DataTable, dataTableToChunkSource } from '@playcanvas/splat-transform';
import { Quat, Vec3 } from 'playcanvas';

import { AddSplatOp } from './edit-ops';
import { EditorSplatResource } from './editor-splat-resource';
import { Events } from './events';
import { estimateCount, generateShape, PLY_LOAD_ROTATION, ShapeParams, toPlyConvention } from './generators';
import { PROPERTIES } from './generators/ply-writer';
import { Scene } from './scene';
import { Splat } from './splat';
import { i18n } from './ui/localization';

// splat count ceiling enforced by the dialog and again here
const MAX_SPLATS = 4_000_000;

// above this count the live preview pauses (each parameter change rebuilds
// the whole shape - multi-second stalls would make the dialog unusable).
// inserting still works up to MAX_SPLATS.
const PREVIEW_MAX_SPLATS = 2_000_000;

// delay between a dialog parameter change and the preview rebuild
const PREVIEW_DEBOUNCE_MS = 150;

type AddShapeOptions = ShapeParams & { flatSplats?: boolean };

// running counter per shape kind for scene panel names ("Sphere 1", ...).
// peek while previewing, commit only when the shape is actually inserted.
const counters: Record<string, number> = {};
const peekName = (kind: string) => `${i18n.t(`popup.shape.kind.${kind}`)} ${(counters[kind] ?? 0) + 1}`;
const commitName = (kind: string) => {
    counters[kind] = (counters[kind] ?? 0) + 1;
};

// build a Splat scene element from generated shape data. the buffers are
// converted to PLY file convention and paired with the same load rotation the
// asset loader applies to PLY files, so generated splats behave identically
// to imported ones in every code path (transform, export, project save).
//
// v3 has no GSplatData: in-memory data enters through a ChunkSource, the same
// abstraction the file loader produces.
const createShapeSplat = async (scene: Scene, params: AddShapeOptions, name: string): Promise<Splat> => {
    const buffers = toPlyConvention(generateShape(params, { flatSplats: params.flatSplats }));

    const table = new DataTable(PROPERTIES.map(property => new Column(property, buffers[property])));
    const source = dataTableToChunkSource(table);
    const resource = await EditorSplatResource.create(scene.graphicsDevice, source);
    const asset = scene.assetLoader.createGSplatAsset(resource, name);

    const { x, y, z, w } = PLY_LOAD_ROTATION;
    return new Splat(asset, new Quat(x, y, z, w));
};

const registerShapeHandler = (events: Events, scene: Scene) => {
    // live preview state: while the shape dialog is open, a preview splat
    // tracks the dialog parameters. the user can move it with the gizmos;
    // its transform survives parameter changes.
    let preview: Splat | null = null;
    let pending: AddShapeOptions | null = null;
    let dialogActive = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // serialize rebuilds so scene.add calls never interleave
    let buildChain: Promise<void> = Promise.resolve();

    const clearTimer = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };

    const destroyPreview = () => {
        if (preview) {
            scene.remove(preview);
            preview.destroy();
            preview = null;
        }
    };

    // build (or rebuild) the preview from `pending`, preserving the user's
    // transform from the previous preview
    const buildOnce = async () => {
        const params = pending;
        if (!params) {
            return;
        }
        const estimate = estimateCount(params);
        if (estimate === 0 || estimate > MAX_SPLATS) {
            return;
        }
        pending = null;

        // point-defined shapes are authored in world space: generate them
        // centered on their centroid and place the element there, so the
        // pivot sits in the body. their position follows the points, so the
        // preview transform is not preserved.
        let position: Vec3 | null = null;
        let buildParams = params;
        if (params.points && params.points.length >= 9) {
            const pts = params.points;
            const n = pts.length / 3;
            let cx = 0;
            let cy = 0;
            let cz = 0;
            for (let i = 0; i < n; i++) {
                cx += pts[i * 3];
                cy += pts[i * 3 + 1];
                cz += pts[i * 3 + 2];
            }
            cx /= n;
            cy /= n;
            cz /= n;
            position = new Vec3(cx, cy, cz);
            buildParams = {
                ...params,
                points: pts.map((value, i) => value - (i % 3 === 0 ? cx : (i % 3 === 1 ? cy : cz)))
            };
        }

        const splat = await createShapeSplat(scene, buildParams, peekName(params.kind));

        if (position) {
            destroyPreview();
            splat.entity.setLocalPosition(position);
        } else if (preview) {
            splat.entity.setLocalPosition(preview.entity.getLocalPosition().clone());
            splat.entity.setLocalRotation(preview.entity.getLocalRotation().clone());
            splat.entity.setLocalScale(preview.entity.getLocalScale().clone());
            destroyPreview();
        }

        await scene.add(splat);

        if (!dialogActive) {
            // dialog closed while the splat was being added: discard
            scene.remove(splat);
            splat.destroy();
            return;
        }

        preview = splat;
        events.fire('selection', splat);
    };

    const enqueueBuild = () => {
        const build = buildChain.then(buildOnce);
        buildChain = build.catch(() => {});
        return build;
    };

    events.on('shape.previewBegin', () => {
        dialogActive = true;
    });

    events.on('shape.dialogChanged', (params: AddShapeOptions) => {
        if (!dialogActive) {
            return;
        }
        clearTimer();
        const estimate = estimateCount(params);
        // nothing to build (a point shape without enough points): remove the
        // previous preview instead of leaving it behind
        if (estimate === 0) {
            pending = null;
            buildChain = buildChain.then(() => destroyPreview());
            return;
        }
        // pause the live preview for very large shapes; the last preview
        // stays visible and inserting builds the real thing
        if (estimate > PREVIEW_MAX_SPLATS) {
            pending = null;
            return;
        }
        pending = params;
        timer = setTimeout(() => {
            timer = null;
            enqueueBuild();
        }, PREVIEW_DEBOUNCE_MS);
    });

    // dialog cancelled: remove the preview
    events.on('shape.previewEnd', () => {
        dialogActive = false;
        clearTimer();
        pending = null;
        buildChain = buildChain.then(() => destroyPreview());
    });

    // dialog confirmed (or called directly): make the preview permanent and
    // record an undo entry
    events.function('shape.add', async (params: AddShapeOptions) => {
        clearTimer();
        // keep the final build from being discarded as a stale preview - also
        // makes 'shape.add' work standalone, without the dialog being open
        dialogActive = true;
        pending = params;
        try {
            await enqueueBuild();
        } finally {
            dialogActive = false;
        }

        const splat = preview;
        preview = null;
        if (!splat) {
            throw new Error(`shape exceeds ${MAX_SPLATS} splats`);
        }

        commitName(params.kind);

        // a point-defined shape consumes its placement points
        if (params.points) {
            events.fire('pointPlace.clear');
        }

        // the splat is already in the scene, so suppress the op's do()
        events.fire('edit.add', new AddSplatOp(scene, splat), true);
        events.fire('selection', splat);
        return splat;
    });
};

export { registerShapeHandler, AddShapeOptions, MAX_SPLATS, PREVIEW_MAX_SPLATS };
